import {
  DataType,
  type SettingTypeMetadata,
  type SettingTypeValueMap,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createSettingsParams,
  type ISettingsParam,
  type PartialBy,
  SettingsParamType,
} from '@wix/tpa-settings';
import {
  AlignmentOptions,
  CourseLandingDate,
  CourseSessionsDisplay,
  CoursesDisplay,
  LayoutTypes,
  SlotsAvailability,
} from '../../types/types';
import { AccessibilityHtmlTags } from '../../utils/a11y/consts';
import { COURSES_EXPERIMENT } from '../../utils/settings/consts';

export const withoutDefaultFallback = (settingsParam: ISettingsParam) => ({
  ...settingsParam,
  getDefaultValue: () => '',
});
export type ISettingsParams = {
  timeSlotsDisplay: SlotsAvailability;
  showClasses: SettingsParamType.Boolean;
  showCourses: SettingsParamType.Boolean;
  // Declared as the enums, not as String, so a radio set cannot be wired to a
  // param whose values it does not hold. Same as `timeSlotsDisplay` above.
  courseLandingDate: CourseLandingDate;
  coursesDisplay: CoursesDisplay;
  courseSessionsDisplay: CourseSessionsDisplay;
  serviceTypeLabel: SettingsParamType.Text;
  courseStatusLabel: SettingsParamType.Text;
  courseEnrollButton: SettingsParamType.Text;
  courseEnrollmentClosedStatus: SettingsParamType.Text;
  courseBadgeText: SettingsParamType.Text;
  headerSticky: SettingsParamType.Boolean;
  limitTimeSlots: SettingsParamType.Boolean;
  showLoadMoreButton: SettingsParamType.Boolean;
  maxTimeSlotsPerDay: SettingsParamType.Number;
  scrollBetweenDays: SettingsParamType.Boolean;
  selectedLocations: SettingsParamType.Text[];
  selectedCustomLocation: SettingsParamType.Boolean;
  selectedCategories: SettingsParamType.Text[];
  titleText: SettingsParamType.Text;
  titleTagName: SettingsParamType.String;
  dateTagName: SettingsParamType.String;
  headerFiltersTagName: SettingsParamType.String;
  tableDatesTagName: SettingsParamType.String;
  sessionsItemsServiceNameTag: SettingsParamType.String;
  sessionItemsRegularButtonNameTag: SettingsParamType.String;
  sessionsItemsPrimaryNameTag: SettingsParamType.String;
  loadMoreButtonNameTag: SettingsParamType.String;
  sessionsItemsSecondaryNameTag: SettingsParamType.String;
  tableDayTagName: SettingsParamType.String;
  selectedServices: SettingsParamType.String[];
  timeSlotsPerDay: SettingsParamType.Number;
  spotsLeftVisibility: SettingsParamType.Boolean;
  noSpotsLeft: SettingsParamType.Text;
  serviceLabel: SettingsParamType.Text;
  staffMemberLabel: SettingsParamType.Text;
  locationLabel: SettingsParamType.Text;
  timeSlotsBeforeLoadMore: SettingsParamType.Number;
  toEarlyToBookIndication: SettingsParamType.Text;
  toLateToBookIndication: SettingsParamType.Text;
  bookOnlineDisabledIndication: SettingsParamType.Text;
  joinWaitlistButton: SettingsParamType.Text;
  pendingApprovalButton: SettingsParamType.Text;
  bookButtonText: SettingsParamType.Text;
  readMoreLinkText: SettingsParamType.Text;
  loadMoreButton: SettingsParamType.Text;
  alreadyBookedButton: SettingsParamType.Text;
  blockedDayText: SettingsParamType.Text;
  layoutStyle: SettingsParamType.String;
  sessionItemsAlignment: SettingsParamType.String;
  sessionItemsAlignmentOverlay: SettingsParamType.String;
};

export type ISettingsParamsDefinitions = {
  [key in keyof ISettingsParams]: PartialBy<
    ISettingsParam<ISettingsParams[key]>,
    'key'
  >;
};

const getMetadata = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
): SettingTypeMetadata => ({
  type: type as DataType.UNKNOWN_DataType, // type assertion is needed to avoid strange TypeScript error
  defaultValue: {
    type: 'static',
    value: defaultValue,
  },
});

const settingsParam = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
) => ({
  getDefaultValue: () => defaultValue,
  metadata: getMetadata(type, defaultValue),
});

const enumSettingsParam = <T extends string>(defaultValue: T) => ({
  getDefaultValue: () => defaultValue,
  metadata: getMetadata(DataType.textEnum, defaultValue),
});

const typedSettingsParam = <T extends DataType, Type extends SettingsParamType>(
  dataType: T,
  type: Type | undefined,
  defaultValue: SettingTypeValueMap[T]['value'],
) => ({
  type,
  ...settingsParam(dataType, defaultValue),
});

const textSettingsParam = (
  defaultValueKey: string,
): Omit<ISettingsParam<SettingsParamType.Text>, 'key'> => ({
  type: SettingsParamType.Text,
  getDefaultValue: ({ t }) => t(defaultValueKey),
  metadata: ({ t }) => ({
    type: DataType.text,
    defaultValue: {
      type: 'static',
      value: t(defaultValueKey),
    },
  }),
});

/**
 * A text default that follows the experiment, so a live site that never edited
 * the title keeps the old string.
 */
const coursesTextSettingsParam = (
  defaultValueKey: string,
  coursesValueKey: string,
): Omit<ISettingsParam<SettingsParamType.Text>, 'key'> => ({
  type: SettingsParamType.Text,
  getDefaultValue: ({ t, experiments }) =>
    t(
      experiments?.enabled(COURSES_EXPERIMENT)
        ? coursesValueKey
        : defaultValueKey,
    ),
  metadata: ({ t }) => ({
    type: DataType.text,
    defaultValue: {
      type: 'static',
      value: t(defaultValueKey),
    },
  }),
});

export const settingsParamsDefinitions: ISettingsParamsDefinitions = {
  sessionItemsAlignment: enumSettingsParam(AlignmentOptions.TOP),
  sessionItemsAlignmentOverlay: enumSettingsParam(AlignmentOptions.BOTTOM),
  timeSlotsDisplay: settingsParam(DataType.number, SlotsAvailability.ALL),
  showClasses: settingsParam(DataType.booleanValue, true),
  /**
   * Opt-in on purpose: a site that never ticked the box keeps the widget it has
   * today even once the experiment opens for it.
   */
  showCourses: settingsParam(DataType.booleanValue, false),
  courseLandingDate: enumSettingsParam(
    CourseLandingDate.FIRST_DATE_WITH_SESSIONS,
  ),
  coursesDisplay: enumSettingsParam(CoursesDisplay.ENROLLABLE_ONLY),
  courseSessionsDisplay: enumSettingsParam(
    CourseSessionsDisplay.LATE_ENROLLMENT_OPEN_ONLY,
  ),
  showLoadMoreButton: settingsParam(DataType.booleanValue, true),
  headerSticky: settingsParam(DataType.booleanValue, true),
  limitTimeSlots: settingsParam(DataType.booleanValue, true),
  maxTimeSlotsPerDay: settingsParam(DataType.number, 6),
  scrollBetweenDays: settingsParam(DataType.booleanValue, true),
  selectedLocations: typedSettingsParam(
    DataType.container,
    SettingsParamType.Object,
    [],
  ),
  selectedCustomLocation: settingsParam(DataType.booleanValue, false),
  selectedCategories: typedSettingsParam(
    DataType.container,
    SettingsParamType.Object,
    [],
  ),
  selectedServices: typedSettingsParam(
    DataType.container,
    SettingsParamType.Object,
    [],
  ),
  timeSlotsPerDay: settingsParam(DataType.number, 10),
  // TODO: final wording is still a content decision. It is one Babel value.
  titleText: coursesTextSettingsParam(
    'daily-agenda.settings.text.title.placeholder',
    'daily-agenda.settings.text.title.placeholder-courses',
  ),
  titleTagName: enumSettingsParam(AccessibilityHtmlTags.SecondaryHeading),
  dateTagName: enumSettingsParam(AccessibilityHtmlTags.SecondaryHeading),
  headerFiltersTagName: enumSettingsParam(
    AccessibilityHtmlTags.SecondaryHeading,
  ),
  tableDatesTagName: enumSettingsParam(AccessibilityHtmlTags.Paragraph),
  tableDayTagName: enumSettingsParam(AccessibilityHtmlTags.Paragraph),
  sessionItemsRegularButtonNameTag: enumSettingsParam(
    AccessibilityHtmlTags.Paragraph,
  ),
  loadMoreButtonNameTag: enumSettingsParam(AccessibilityHtmlTags.Paragraph),
  spotsLeftVisibility: settingsParam(DataType.booleanValue, true),
  sessionsItemsServiceNameTag: enumSettingsParam(
    AccessibilityHtmlTags.MainHeading,
  ),
  sessionsItemsPrimaryNameTag: enumSettingsParam(
    AccessibilityHtmlTags.Paragraph,
  ),
  sessionsItemsSecondaryNameTag: enumSettingsParam(
    AccessibilityHtmlTags.Paragraph,
  ),
  noSpotsLeft: textSettingsParam(
    'daily-agenda.settings.text.button.no-availability-text.placeholder',
  ),
  serviceLabel: textSettingsParam(
    'daily-agenda.settings.text.filters-and-preferences.service-label.placeholder',
  ),
  staffMemberLabel: textSettingsParam(
    'daily-agenda.settings.text.filters-and-preferences.staff-member-label.placeholder',
  ),
  locationLabel: textSettingsParam(
    'daily-agenda.settings.text.filters-and-preferences.location-label.placeholder',
  ),
  layoutStyle: enumSettingsParam(LayoutTypes.LIST),
  timeSlotsBeforeLoadMore: settingsParam(DataType.number, 12),
  toEarlyToBookIndication: textSettingsParam(
    'daily-agenda.settings.text.button.to-early-to-book.placeholder',
  ),
  toLateToBookIndication: textSettingsParam(
    'daily-agenda.settings.text.button.to-late-to-book.placeholder',
  ),
  bookOnlineDisabledIndication: textSettingsParam(
    'app.settings.defaults.button.more-info',
  ),
  joinWaitlistButton: textSettingsParam(
    'daily-agenda.settings.text.button.wait-list.placeholder',
  ),
  pendingApprovalButton: textSettingsParam(
    'app.settings.defaults.booking-details.pending-approval.text',
  ),
  bookButtonText: textSettingsParam(
    'daily-agenda.settings.text.button.book-button.placeholder',
  ),
  readMoreLinkText: textSettingsParam('daily-agenda.agenda-item.read-more'),
  loadMoreButton: textSettingsParam(
    'daily-agenda.settings.text.button.load-more-tome-slots.placeholder',
  ),
  alreadyBookedButton: textSettingsParam(
    'daily-agenda.settings.text.button.already-booked.placeholder',
  ),
  blockedDayText: textSettingsParam(
    'daily-agenda.settings.text.table-slots.blocked-day.placeholder',
  ),
  serviceTypeLabel: textSettingsParam(
    'daily-agenda.settings.text.filters.service-type-label.placeholder',
  ),
  courseStatusLabel: textSettingsParam(
    'daily-agenda.settings.text.filters.course-status-label.placeholder',
  ),
  courseEnrollButton: textSettingsParam(
    'daily-agenda.settings.text.button.course-enroll-button.placeholder',
  ),
  courseEnrollmentClosedStatus: textSettingsParam(
    'daily-agenda.settings.text.button.course-enrollment-closed.placeholder',
  ),
  // The same key the badge used to render directly, so the word on screen does
  // not change for a site that never opens the Text tab.
  courseBadgeText: textSettingsParam('daily-agenda.agenda-item.course-badge'),
};

export default createSettingsParams<ISettingsParams>(settingsParamsDefinitions);
